import React, { useCallback, useEffect, useState } from 'react';
import { fetchAdminStats, fetchUsageReportSummary } from '../../api/adminClient';
import UserManagement from '../management/UserManagement';
import Reporting from '../management/Reporting';
import SubscriptionsAndTransactions from '../management/SubscriptionsAndTransactions';

const ANALYTICS_TABS = [
  { key: 'reporting', label: 'Reporting' },
  { key: 'users', label: 'User Management' },
  { key: 'subscriptions', label: 'Subscription' },
];

const placeholderAnalyticsStats = [
  { title: 'Total Users', value: 0, change: '—', accent: 'primary', progress: 0 },
  { title: 'Active Users', value: 0, change: '—', accent: 'success', progress: 0 },
  { title: 'Reports Generated', value: 0, change: '—', accent: 'info', progress: 0 },
  { title: 'Clients Created', value: 0, change: '—', accent: 'primary', progress: 0 },
  { title: 'Active Subscriptions', value: 0, change: '—', accent: 'success', progress: 0 },
  { title: 'Freemium Users', value: 0, change: '—', accent: 'info', progress: 0 },
  { title: 'Trialing Accounts', value: 0, change: '—', accent: 'warning', progress: 0 },
];

const Analytics = ({
  users,
  usersLoading,
  usersPagination,
  usersStatusFilter,
  usersSort,
  onUsersPageChange,
  onUsersStatusChange,
  onUsersEmailSearch,
  onUsersDateSearch,
  onUsersSortChange,
  onRefreshUsers,
}) => {
  const [analyticsStats, setAnalyticsStats] = useState(placeholderAnalyticsStats);
  const [activeTab, setActiveTab] = useState('reporting');

  const load = useCallback(async () => {
    try {
      const [statsRes, summaryRes] = await Promise.all([
        fetchAdminStats(),
        fetchUsageReportSummary(),
      ]);

      const s = statsRes.data?.success ? statsRes.data.stats : null;
      const summary = summaryRes.data?.success ? summaryRes.data.summary : null;
      if (!s || !summary) {
        return;
      }

      setAnalyticsStats([
        {
          title: 'Total Users',
          value: s.total_users,
          change: `+${s.new_users_this_month} this month`,
          accent: 'primary',
          progress: Math.min(s.total_users * 2, 100),
        },
        {
          title: 'Active Users',
          value: summary.active_users_30d,
          change: 'Active in last 30 days',
          accent: 'success',
          progress: Math.min(summary.active_users_30d * 2, 100),
        },
        {
          title: 'Reports Generated',
          value: summary.total_reports_created,
          change: `+${s.reports_this_month} this month`,
          accent: 'info',
          progress: Math.min(summary.total_reports_created / 5, 100),
        },
        {
          title: 'Clients Created',
          value: summary.total_clients_created,
          change: 'Across all accounts',
          accent: 'primary',
          progress: Math.min(summary.total_clients_created / 3, 100),
        },
        {
          title: 'Active Subscriptions',
          value: s.active_subscriptions,
          change: 'Paid, in good standing',
          accent: 'success',
          progress: Math.min(s.active_subscriptions * 8, 100),
        },
        {
          title: 'Freemium Users',
          value: s.freemium_users,
          change: s.freemium_users > 0 ? 'on free minutes' : '—',
          accent: 'info',
          progress: Math.min(s.freemium_users * 10, 100),
        },
        {
          title: 'Trialing Accounts',
          value: s.trialing_org_subscriptions,
          change: `${s.trialing_org_subscriptions_expiring_soon} expiring this week`,
          accent: 'warning',
          progress: Math.min(s.trialing_org_subscriptions * 10, 100),
        },
      ]);
    } catch (error) {
      console.error('Failed to load analytics stats:', error);
      setAnalyticsStats(placeholderAnalyticsStats);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="dashboard-view">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h1 className="display-5 fw-semibold mb-1">Analytics</h1>
          <p className="text-muted mb-0">
            Product usage, billing status, and pipeline conversion in one view.
          </p>
        </div>
      </div>

      <div className="stats-grid">
        {analyticsStats.map((stat) => (
          <div key={stat.title}>
            <div className="card-soft h-100 d-flex flex-column justify-content-between">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <span className="badge shadow-sm rounded-pill bg-white text-uppercase text-muted small fw-semibold">
                    {stat.title}
                  </span>
                  <h2 className="display-6 fw-semibold mt-3">{stat.value}</h2>
                </div>
                <span className={`trend-indicator text-${stat.accent}`}>{stat.change}</span>
              </div>
              <div className="progress rounded-pill shadow-sm" role="progressbar">
                <div
                  className={`progress-bar bg-${stat.accent}`}
                  style={{ width: `${stat.progress || 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card-soft">
        <ul className="nav nav-tabs border-0 gap-2 mb-0 px-1 pt-1" role="tablist">
          {ANALYTICS_TABS.map((tab) => (
            <li key={tab.key} className="nav-item" role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`nav-link rounded-4 shadow-sm border-0 px-4 ${activeTab === tab.key ? 'active fw-semibold' : 'text-muted'}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="tab-content p-3 pt-4 border-top border-light mt-2">
          {activeTab === 'reporting' && <Reporting />}
          {activeTab === 'users' && (
            <UserManagement
              users={users}
              loading={usersLoading}
              pagination={usersPagination}
              statusFilter={usersStatusFilter}
              sortFilter={usersSort}
              onStatusChange={onUsersStatusChange}
              onPageChange={onUsersPageChange}
              onSearchChange={onUsersEmailSearch}
              onDateChange={onUsersDateSearch}
              onSortChange={onUsersSortChange}
              onRefreshUsers={onRefreshUsers}
            />
          )}
          {activeTab === 'subscriptions' && <SubscriptionsAndTransactions />}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
